import { useLazyGetFundsFacetsQuery } from '@/store/fundCentre/services/fundCentreAPI';
import { formatFiltersForReq } from 'hooks/FilterHelper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useHelpers from 'hooks/helpers';
import { useDispatch } from 'react-redux';
import { fundCentreActions } from '@/store/fundCentre';
import { useSitecoreContext } from './useSitecoreContext';
import { useRouter } from 'next/router';

export type FilterOption = {
  aggregateCount: number;
  applied: boolean;
  count: number;
  id: string;
  numberOfSelected: number;
  selected: boolean;
  showAggregateCount: boolean;
  text: string;
};

export type Filter = {
  id: string;
  displayNameKey: string;
  options: FilterOption[];
  name: string;
  label: string;
  displayName: string;
};

type Props = {
  notShowAggregateCount?: boolean;
  needFacets?: boolean;
  filters: any;
};

const formatFilters = (filters, showAggregateCount) => {
  return JSON.parse(JSON.stringify(filters)).map((filter) => {
    if (!!filter.options) {
      filter.options.forEach((option) => {
        option.selected = option.selected || false;
        option.numberOfSelected = 0;
        option.applied = option.applied || false;
        option.aggregateCount = option.count;
        option.showAggregateCount = showAggregateCount;
        option.id = option.id || option.text;
      });
    } else {
      filter.options = [];
    }
    return filter;
  });
};

const useFilters = ({ notShowAggregateCount, needFacets, filters }: Props) => {
  const showAggregateCount = !notShowAggregateCount;
  const router = useRouter();
  const { query } = router;
  const { updateUrlParams } = useHelpers();
  const dispatch = useDispatch();
  const [formattedFilters, setFormattedFilters] = useState<Filter[]>(
    formatFilters(filters, showAggregateCount)
  );
  const { sitecoreContext: context } = useSitecoreContext();

  const [getFacets, { isFetching }] = useLazyGetFundsFacetsQuery();

  const mapFacetsData = (facets, filterId) => {
    const queryParams = updateUrlParams();
    if (!formattedFilters.find((filter) => filter.id === filterId)?.options.length) {
      const updated = formattedFilters
        .map((filter) => {
          return {
            ...filter,
            options: facets.find((facet) => facet.name === filter.id).value ?? [],
          };
        })
        .map((filter) => {
          // check if option should be preselected based on URL query
          return {
            ...filter,
            options: filter.options.map((option) => {
              const isPreSelected = !!queryParams[filter.id]?.split(',').includes(option.text);
              return {
                ...option,
                selected: isPreSelected,
                applied: isPreSelected,
              };
            }),
          };
        });
      setFormattedFilters(formatFilters(updated, showAggregateCount));
    }
  };

  const appliedOptions = useMemo(() => {
    return formattedFilters
      .map((filter) => filter.options?.filter((option) => option.applied))
      .flat()
      .filter((option) => option)
      .sort((a, b) => a.numberOfSelected - b.numberOfSelected);
  }, [formattedFilters]);

  const selectedOptions = useMemo(() => {
    return formattedFilters
      .map((filter) => filter.options?.filter((option) => option.selected))
      .flat()
      .filter((option) => option)
      .sort((a, b) => a.numberOfSelected - b.numberOfSelected);
  }, [formattedFilters]);

  const allOptions = useMemo(() => {
    return formattedFilters
      .map((filter) => filter.options?.filter((option) => option.aggregateCount !== null))
      .flat()
      .filter((option) => option);
  }, [formattedFilters]);

  const resetPaginationData = () => {
    dispatch(fundCentreActions.setCurrentPage(1));
    dispatch(
      fundCentreActions.setPaginationData({
        take: 20,
        skip: 0,
      })
    );
    updateUrlParams([
      {
        name: 'page',
        value: 1,
      },
    ]);
  };

  const onFilterApply = () => {
    const updatedFilters = formattedFilters.map((filter) => {
      return {
        ...filter,
        options: filter.options.map((option) => {
          if (option.selected) {
            return { ...option, applied: true };
          }
          return { ...option, applied: false };
        }),
      };
    });

    resetPaginationData();
    setFormattedFilters(updatedFilters);
    saveFilterToUrl(formattedFilters);
    setFilters(updatedFilters);
  };

  const unApplyOptions = (singleOption?: FilterOption) => {
    let updatedFilters;
    if (singleOption) {
      updatedFilters = formattedFilters.map((filter) => {
        return {
          ...filter,
          options: filter.options.map((option) => {
            if (option.text === singleOption.text) {
              return { ...option, applied: false, selected: false };
            }
            return {
              ...option,
            };
          }),
        };
      });
    } else {
      updatedFilters = formattedFilters.map((filter) => {
        return {
          ...filter,
          options: filter.options.map((option) => {
            if (selectedOptions.includes(option)) {
              return { ...option, applied: false, selected: false };
            }
            return {
              ...option,
            };
          }),
        };
      });
      resetPaginationData();
    }
    // reset DatePicker value
    dispatch(fundCentreActions.setSelectedDate(null));
    updateUrlParams([{ name: 'date', value: '' }]);

    saveFilterToUrl(updatedFilters);
    setFormattedFilters(updatedFilters);
    setFilters(updatedFilters);
  };

  const maxNumInSelectedOptions = useMemo(() => {
    return selectedOptions[selectedOptions.length - 1]?.numberOfSelected + 1 || 0;
  }, [selectedOptions]);

  const onOptionSelect = useCallback(
    (targetOption) => {
      const updatedFilters = formattedFilters.map((filter) => {
        return {
          ...filter,
          options: filter.options.map((option) => {
            if (option.text === targetOption.text) {
              const isSelected = !option.selected;
              return {
                ...option,
                selected: isSelected,
                numberOfSelected: isSelected ? maxNumInSelectedOptions : 0,
              };
            }
            return option;
          }),
        };
      });

      setFormattedFilters(updatedFilters);
    },
    [formattedFilters, maxNumInSelectedOptions]
  );

  const onResetUnAppliedOptions = useCallback(() => {
    const updatedFilters = formattedFilters.map((filter) => {
      return {
        ...filter,
        options: filter.options.map((option) => ({
          ...option,
          selected: option.applied,
        })),
      };
    });
    setFormattedFilters(updatedFilters);
  }, [formattedFilters]);

  const saveFilterToUrl = useCallback(
    (filters) => {
      const selectedFilters = filters.map((filter) => {
        return {
          filterName: filter.id,
          selectedOptions: filter.options
            .filter((option) => option.selected)
            .map((selectedOption) => selectedOption.id),
        };
      });
      const params = selectedFilters.map((filter) => {
        return {
          name: filter.filterName,
          value: filter.selectedOptions.join(','),
        };
      });
      updateUrlParams(params);
    },
    [updateUrlParams]
  );

  const onClearFilterSelection = useCallback(
    (filterToClear: Filter) => {
      const updatedFilters = formattedFilters.map((filter) => {
        if (filter.id !== filterToClear.id) return filter;

        return {
          ...filter,
          options: filter.options.map((option) => ({
            ...option,
            selected: false,
            numberOfSelected: 0,
            applied: false,
          })),
        };
      });

      setFormattedFilters(updatedFilters);
      saveFilterToUrl(updatedFilters);

      setFilters(updatedFilters);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formattedFilters, saveFilterToUrl]
  );

  const updateAggregateCount = useCallback((facets) => {
    setFormattedFilters((prevFilters) =>
      prevFilters.map((filter) => {
        const sameFacet = facets.find((facet) => facet.name === filter.id) || { value: [] };

        const updatedOptions = filter.options.map((option) => {
          const matchedFacetOption = sameFacet.value.find(
            (facetOption) => facetOption.text === option.text
          );
          return {
            ...option,
            aggregateCount: matchedFacetOption?.count ?? 0,
          };
        });

        return {
          ...filter,
          options: updatedOptions,
        };
      })
    );
  }, []);

  const fetchFacets = async (filterId) => {
    const { search } = updateUrlParams();
    const { fundCentreConfiguration, language, site } = context;
    const { countryCode, countryInvestors, literatureAuthorizations } = fundCentreConfiguration;
    const { reqFacets, reqFilters } = formatFiltersForReq(formattedFilters, false);

    const reqFiltersWithOutSameFilterId = reqFilters.map((obj) => {
      if (obj.name === filterId) {
        return { name: obj.name, values: [] };
      }
      return obj;
    });

    const payload = {
      ...{ searchQuery: { id: '', name: search ? decodeURIComponent(search) : '' } },
      ...{
        filters: reqFiltersWithOutSameFilterId,
      },
      site: site.name,
      countryInvestors,
      countryCode,
      language,
      literatureAuthorizations,
      ...{ facets: reqFacets },
    };

    const { data } = await getFacets(payload);
    mapFacetsData(data, filterId);
    updateAggregateCount(data);
  };

  const setFilters = (filters) => {
    const { reqFilters } = formatFiltersForReq(filters, false);
    dispatch(fundCentreActions.setFilters(reqFilters));
  };

  useEffect(() => {
    const { search } = updateUrlParams();
    dispatch(
      fundCentreActions.setSearchQuery({
        id: '',
        name: search ? decodeURIComponent(search) : '',
      })
    );
    const filtersFromUrl = filters.map((filter) => {
      return {
        name: filter.id,
        values: query[filter.id]?.split(',') || [],
      };
    });
    if (filtersFromUrl.some((filter) => filter.values.length !== 0)) {
      dispatch(fundCentreActions.setFilters(filtersFromUrl));
    }
    if (needFacets) {
      fetchFacets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    showAggregateCount,
    formattedFilters,
    isFetching,
    fetchFacets,
    onOptionSelect,
    appliedOptions,
    allOptions,
    onClearFilterSelection,
    onResetUnAppliedOptions,
    selectedOptions,
    onFilterApply,
    unApplyOptions,
  };
};

export default useFilters;
